import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';
import type { MedicationKind } from '@/lib/medicationSuggestions';

/**
 * A patient's medication, as the Medications tab shows it
 * (PatientMedicationService::present on the API).
 *
 * `source` is `eprescribed` for a row a ScriptSure prescription added. On
 * those rows the drug fields are what was sent and the API refuses to change
 * them; staff can still stop or remove the row and edit its notes.
 *
 * Dates are calendar dates ('YYYY-MM-DD') or null. Show them with
 * `formatDate` (lib/utils), never `new Date()`: a bare date parses as UTC
 * midnight and shows a day early in Pacific time.
 */
export interface PatientMedication {
  id: number;
  medicationName: string;
  strength: string;
  directions: string;
  startDate: string | null;
  endDate: string | null;
  status: 'active' | 'stopped';
  prescriberName: string;
  notes: string;
  kind: MedicationKind;
  /** RxNorm code of the drug and strength (from a picked suggestion or the e-prescription), '' when none. */
  rxnorm: string;
  /** 'patient' = added by the patient in HealthNexus; staff edit it like a manual row. */
  source: 'manual' | 'eprescribed' | 'patient';
  createdAt: string;
  updatedAt: string;
}

export interface PatientMedicationInput {
  medicationName: string;
  strength: string;
  directions: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'stopped';
  prescriberName: string;
  notes: string;
  kind: MedicationKind;
  /** RxNorm code of a picked drug and strength; send '' when the name or strength was typed. */
  rxnorm: string;
}

export function patientMedicationsKey(patientId: number) {
  return ['patient-medications', patientId] as const;
}

export function listPatientMedications(patientId: number): Promise<PatientMedication[]> {
  return apiClient
    .get<ApiResponse<{ medications: PatientMedication[] }>>(`/patients/${patientId}/medications`)
    .then((r) => r.data.data.medications);
}

export function createPatientMedication(
  patientId: number,
  input: PatientMedicationInput,
): Promise<PatientMedication> {
  return apiClient
    .post<ApiResponse<{ medication: PatientMedication }>>(`/patients/${patientId}/medications`, input)
    .then((r) => r.data.data.medication);
}

export function updatePatientMedication(
  patientId: number,
  medicationId: number,
  input: Partial<PatientMedicationInput>,
): Promise<PatientMedication> {
  return apiClient
    .put<ApiResponse<{ medication: PatientMedication }>>(
      `/patients/${patientId}/medications/${medicationId}`,
      input,
    )
    .then((r) => r.data.data.medication);
}

/** Hide a medication entered in error (kept on the server, never destroyed). */
export function removePatientMedication(patientId: number, medicationId: number): Promise<void> {
  return apiClient.delete(`/patients/${patientId}/medications/${medicationId}`).then(() => undefined);
}

